import React, { useEffect, useMemo, useState } from 'react';
import { useVideos, toEmbedUrl } from '../hooks/useVideos';
import { Search, SlidersHorizontal, X, Video, Play } from 'lucide-react';

const pageSize = 24;

/* ─── Video Thumbnail ─── */
function VideoThumb({ video }) {
  const [imgError, setImgError] = useState(false);

  // YouTube otomatik thumbnail
  const autoThumb = useMemo(() => {
    const yt = video.videoUrl?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
    if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
    const shorts = video.videoUrl?.match(/youtube\.com\/shorts\/([^?&/]+)/);
    if (shorts) return `https://img.youtube.com/vi/${shorts[1]}/hqdefault.jpg`;
    return null;
  }, [video.videoUrl]);

  const thumb = (!imgError && (video.thumbnailUrl || autoThumb)) || null;

  if (thumb) {
    return (
      <img
        src={thumb}
        alt={video.title}
        loading="lazy"
        onError={() => setImgError(true)}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
      <Video className="w-10 h-10 text-slate-500" />
    </div>
  );
}

/* ─── Video Player Modal ─── */
function VideoModal({ video, onClose }) {
  const embedUrl = toEmbedUrl(video.videoUrl);
  const isEmbed = embedUrl && (embedUrl.includes('youtube.com/embed') || embedUrl.includes('vimeo.com'));
  const isFile  = video.videoUrl && !isEmbed;

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl bg-black rounded-2xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Video */}
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          {isEmbed ? (
            <iframe
              src={`${embedUrl}?autoplay=1`}
              title={video.title}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          ) : isFile ? (
            <video
              src={video.videoUrl}
              controls
              autoPlay
              className="absolute inset-0 w-full h-full object-contain bg-black"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
              Video yüklenemedi
            </div>
          )}
        </div>

        {/* Bilgi alanı */}
        <div className="bg-white p-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 text-sm leading-snug">{video.title}</h3>
            {video.description && (
              <p className="mt-1.5 text-xs text-slate-500 line-clamp-2">{video.description}</p>
            )}
            <p className="mt-1 text-[11px] text-slate-400">{formatHumanDate(video.createdAt)}</p>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition"
          >
            <X className="w-4 h-4 text-slate-600" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Ana Sayfa ─── */
export default function VideosPage() {
  const { videos: all, loading } = useVideos();

  const [q, setQ] = useState('');
  const [qDeb, setQDeb] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setQDeb(q.trim().toLocaleLowerCase('tr')), 300);
    return () => clearTimeout(t);
  }, [q]);

  const filtered = useMemo(() => {
    let arr = all;
    if (qDeb) arr = arr.filter(v =>
      `${v.title} ${v.description}`.toLocaleLowerCase('tr').includes(qDeb)
    );
    if (sortBy === 'newest')     return [...arr].sort((a, b) => b.createdAt - a.createdAt);
    if (sortBy === 'title-asc')  return [...arr].sort((a, b) => (a.title || '').localeCompare(b.title || '', 'tr'));
    if (sortBy === 'title-desc') return [...arr].sort((a, b) => (b.title || '').localeCompare(a.title || '', 'tr'));
    return arr;
  }, [all, qDeb, sortBy]);

  useEffect(() => setPage(1), [qDeb, sortBy]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page]);

  const clearAll = () => { setQ(''); setSortBy('newest'); };

  const chips = [
    qDeb && { k: 'Arama', v: q, onClear: () => setQ('') },
    sortBy !== 'newest' && { k: 'Sıralama', v: sortBy === 'title-asc' ? 'A-Z' : 'Z-A', onClear: () => setSortBy('newest') },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-50">
      {modal && <VideoModal video={modal} onClose={() => setModal(null)} />}

      <div className="container mx-auto px-4 py-6 max-w-screen-xl">

        {/* Başlık */}
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Video Arşivi</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            <span className="font-medium text-slate-700">{filtered.length.toLocaleString('tr')}</span> video
          </p>
        </div>

        {/* Filtre barı */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-5">
          <div className="p-3 flex items-center gap-2">

            {/* Arama */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Başlık veya açıklama ara…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-slate-900 bg-slate-50 placeholder:text-slate-400"
              />
              {q && (
                <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Sıralama */}
            <div className="relative shrink-0 w-36">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="w-full appearance-none pl-3 pr-7 py-2 text-sm bg-white border border-slate-200 rounded-lg
                  text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 transition"
              >
                <option value="newest">En Yeni</option>
                <option value="title-asc">A → Z</option>
                <option value="title-desc">Z → A</option>
              </select>
              <svg className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {/* Filtre toggle */}
            <button
              onClick={() => setFiltersOpen(o => !o)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition shrink-0 ${
                chips.length > 0
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="hidden sm:inline">Filtrele</span>
              {chips.length > 0 && (
                <span className="text-xs rounded-full px-1.5 py-0.5 font-medium bg-white text-slate-900">
                  {chips.length}
                </span>
              )}
            </button>
          </div>

          {/* Genişleyen filtreler */}
          {filtersOpen && chips.length > 0 && (
            <div className="border-t border-slate-100 p-3 flex justify-end">
              <button onClick={clearAll} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 transition">
                <X className="w-3 h-3" /> Tümünü sıfırla
              </button>
            </div>
          )}

          {/* Chip'ler */}
          {chips.length > 0 && (
            <div className={`flex flex-wrap gap-1.5 px-3 pb-3 ${filtersOpen ? '' : 'border-t border-slate-100 pt-3'}`}>
              {chips.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-slate-100 text-slate-700 border border-slate-200">
                  <span className="font-medium">{c.k}:</span> {c.v}
                  <button onClick={c.onClear} className="ml-0.5 rounded-full p-0.5 hover:bg-slate-200 transition">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Skeleton */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-xl overflow-hidden animate-pulse">
                <div className="h-44 bg-slate-200" />
                <div className="bg-white p-3 space-y-2">
                  <div className="h-3 bg-slate-100 rounded w-3/4" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Grid */}
        {!loading && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
              {paged.map(v => (
                <article
                  key={v.id}
                  onClick={() => setModal(v)}
                  className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 hover:shadow-md transition-all duration-150 cursor-pointer"
                >
                  {/* Thumbnail */}
                  <div className="relative overflow-hidden bg-slate-900 h-44">
                    <VideoThumb video={v} />

                    {/* Karartma + play butonu */}
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg
                        scale-90 group-hover:scale-100 transition-transform duration-200">
                        <Play className="w-5 h-5 text-slate-900 ml-0.5" fill="currentColor" />
                      </div>
                    </div>

                    {/* Süre badge (eğer varsa) */}
                    <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-medium">
                      {formatHumanDate(v.createdAt)}
                    </div>
                  </div>

                  {/* Bilgi */}
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-slate-800 line-clamp-2 leading-snug">{v.title}</h3>
                    {v.description && (
                      <p className="mt-1 text-xs text-slate-400 line-clamp-2">{v.description}</p>
                    )}
                  </div>
                </article>
              ))}

              {paged.length === 0 && (
                <div className="col-span-full flex flex-col items-center justify-center py-16 text-slate-400">
                  <Video className="w-10 h-10 mb-3 opacity-40" />
                  <p className="text-sm">Video bulunamadı.</p>
                  <button onClick={clearAll} className="mt-3 text-xs text-slate-500 underline underline-offset-2 hover:text-slate-800">
                    Filtreleri temizle
                  </button>
                </div>
              )}
            </div>

            {/* Sayfalama */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-1.5">
                <button onClick={() => setPage(x => Math.max(1, x - 1))} disabled={page === 1}
                  className="px-3 py-2 text-sm rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition">
                  ← Önceki
                </button>
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let n;
                  if (totalPages <= 7) n = i + 1;
                  else if (page <= 4) n = i + 1;
                  else if (page >= totalPages - 3) n = totalPages - 6 + i;
                  else n = page - 3 + i;
                  return (
                    <button key={n} onClick={() => setPage(n)}
                      className={`w-9 h-9 text-sm rounded-lg border transition ${
                        page === n ? 'bg-slate-900 text-white border-slate-900 font-medium' : 'border-slate-200 hover:bg-slate-50 text-slate-600'
                      }`}>
                      {n}
                    </button>
                  );
                })}
                <button onClick={() => setPage(x => Math.min(totalPages, x + 1))} disabled={page === totalPages}
                  className="px-3 py-2 text-sm rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition">
                  Sonraki →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function formatHumanDate(d) {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  const diff = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
  if (diff > 365) return `${date.getFullYear()}`;
  if (diff > 30)  return `${Math.floor(diff / 30)} ay önce`;
  if (diff > 1)   return `${Math.floor(diff)} gün önce`;
  return 'Bugün';
}